import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logix2Dock } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — LOGIX-2™ Voice Controls (Phase 20)
 *
 * Read Aloud / Pause / Stop / Replay for any LOGIX-2 assistant response.
 *  - Default: browser SpeechSynthesis (free, no key).
 *  - When backend returns provider="elevenlabs", plays returned base64 mp3.
 *  - Compliance-guarded server-side; client honours sanitised text.
 *
 * Usage:  <Logix2VoiceControls text={assistantResponseText} compact />
 */

function strip(text) {
  if (!text) return "";
  // Strip markdown and emoji from spoken text to keep voice clean.
  return String(text)
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#>*_`~|]/g, " ")
    .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export default function Logix2VoiceControls({
  text = "",
  compact = false,
  autoRead = false,
  className = "",
  onState,
} = {}) {
  const [state, setState] = useState("idle"); // idle | playing | paused | ended | error
  const [provider, setProvider] = useState("browser");
  const [supports, setSupports] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const audioRef = useRef(null);
  const utterRef = useRef(null);
  const lastTextRef = useRef("");

  useEffect(() => {
    const browserOk = typeof window !== "undefined" && "speechSynthesis" in window;
    setSupports(browserOk);
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { onState && onState(state); }, [state, onState]);

  const announceVisible = useCallback((s) => {
    setState(s);
  }, []);

  const stop = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    } catch { /* ignore */ }
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } catch { /* ignore */ }
    announceVisible("idle");
  }, [announceVisible]);

  const playBrowser = useCallback((spoken, rate = 1.0, pitch = 1.0, voiceName) => {
    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(spoken || "");
    utter.rate = Math.max(0.5, Math.min(2.0, Number(rate) || 1.0));
    utter.pitch = Math.max(0.5, Math.min(2.0, Number(pitch) || 1.0));
    utter.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
    try {
      const voices = synth.getVoices() || [];
      if (voiceName && voiceName !== "default") {
        const v = voices.find((vv) => vv.name === voiceName);
        if (v) utter.voice = v;
      }
    } catch { /* ignore */ }
    utter.onend = () => announceVisible("ended");
    utter.onerror = (e) => { setError(e?.error || "speech_error"); announceVisible("error"); };
    utterRef.current = utter;
    synth.cancel();
    synth.speak(utter);
    announceVisible("playing");
  }, [announceVisible]);

  const playRemote = useCallback((b64, mime) => {
    try {
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime || "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onplay = () => announceVisible("playing");
      audio.onended = () => { announceVisible("ended"); URL.revokeObjectURL(url); };
      audio.onpause = () => announceVisible(audio.currentTime > 0 ? "paused" : "idle");
      audio.onerror = () => { announceVisible("error"); setError("audio_error"); URL.revokeObjectURL(url); };
      audioRef.current = audio;
      audio.play().catch((e) => { setError(e?.message || "play_failed"); announceVisible("error"); });
    } catch (e) {
      setError(e?.message || "audio_decode_failed");
      announceVisible("error");
    }
  }, [announceVisible]);

  const speak = useCallback(async () => {
    if (busy) return;
    if (!text || !text.trim()) return;
    setError("");
    setBusy(true);
    try {
      stop();
      // Pull voice settings (provider, rate, pitch, voice_name, summary_only)
      let settings = { provider: "browser", rate: 1.0, pitch: 1.0, voice_name: "default", summary_only: true };
      try {
        const { data } = await logix2Dock.voiceSettings();
        settings = { ...settings, ...(data?.settings || {}) };
      } catch { /* ignore — keep defaults */ }

      // Ask backend to sanitise text and prepare TTS payload (browser or elevenlabs).
      const cleanedInput = strip(text);
      lastTextRef.current = cleanedInput;
      const { data: payload } = await logix2Dock.voiceSpeak({
        text: cleanedInput,
        provider: settings.provider,
        voice_name: settings.voice_name,
        rate: settings.rate,
        pitch: settings.pitch,
        summary_only: settings.summary_only,
      });
      setProvider(payload?.provider || "browser");
      if (payload?.provider === "elevenlabs" && payload?.audio_b64) {
        playRemote(payload.audio_b64, payload.audio_mime || "audio/mpeg");
      } else if (payload?.provider === "disabled") {
        setError("voice_disabled_in_settings");
        announceVisible("error");
      } else {
        playBrowser(payload?.text || payload?.spoken_text || cleanedInput, payload?.rate, payload?.pitch, payload?.voice_name);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (e?.message || "speak_failed"));
      announceVisible("error");
    } finally {
      setBusy(false);
    }
  }, [text, busy, stop, playBrowser, playRemote, announceVisible]);

  const pauseToggle = useCallback(() => {
    if (provider === "elevenlabs" && audioRef.current) {
      if (audioRef.current.paused) audioRef.current.play();
      else audioRef.current.pause();
      return;
    }
    try {
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        window.speechSynthesis.pause();
        announceVisible("paused");
      } else if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
        announceVisible("playing");
      }
    } catch { /* ignore */ }
  }, [provider, announceVisible]);

  const replay = useCallback(() => {
    if (provider === "elevenlabs" && audioRef.current) {
      try { audioRef.current.currentTime = 0; audioRef.current.play(); announceVisible("playing"); } catch { /* ignore */ }
      return;
    }
    speak();
  }, [provider, speak, announceVisible]);

  // Auto-read when requested AND the text changes.
  // Always stop any in-flight speech before starting the new one (voice interruption fix).
  useEffect(() => {
    if (!autoRead) return;
    if (!text || !text.trim()) return;
    if (text === lastTextRef.current) return;
    // Stop prior speech / audio and reset state so the next utterance starts cleanly.
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } catch { /* ignore */ }
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    } catch { /* ignore */ }
    setError("");
    setState("idle");
    speak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRead, text]);

  const isPlaying = state === "playing";
  const isPaused = state === "paused";
  const hasText = !!(text && text.trim());

  return (
    <div className={`l2v-controls ${compact ? "is-compact" : ""} ${className}`} data-testid="logix2-voice-controls">
      <button
        type="button"
        className="l2v-btn l2v-btn--read"
        onClick={speak}
        disabled={busy || !hasText || !supports}
        aria-label="Read this response aloud"
        title="Read aloud"
        data-testid="logix2-voice-read"
      >
        {busy ? "…" : "🔊 Read Aloud"}
      </button>
      <button
        type="button"
        className="l2v-btn"
        onClick={pauseToggle}
        disabled={state === "idle" || state === "ended" || state === "error"}
        aria-label={isPaused ? "Resume voice" : "Pause voice"}
        title={isPaused ? "Resume" : "Pause"}
        data-testid="logix2-voice-pause"
      >
        {isPaused ? "▶ Resume" : "⏸ Pause"}
      </button>
      <button
        type="button"
        className="l2v-btn"
        onClick={stop}
        disabled={state === "idle"}
        aria-label="Stop voice"
        title="Stop"
        data-testid="logix2-voice-stop"
      >
        ⏹ Stop
      </button>
      <button
        type="button"
        className="l2v-btn"
        onClick={replay}
        disabled={busy || !hasText || !supports}
        aria-label="Replay voice from the start"
        title="Replay"
        data-testid="logix2-voice-replay"
      >
        ↻ Replay
      </button>
      {isPlaying && <span className="l2v-state l2v-state--playing" aria-live="polite">Speaking{provider === "elevenlabs" ? " via ElevenLabs…" : "…"}</span>}
      {isPaused && <span className="l2v-state l2v-state--paused" aria-live="polite">Paused</span>}
      {state === "ended" && <span className="l2v-state l2v-state--ended" aria-live="polite">Finished.</span>}
      {state === "error" && <span className="l2v-state l2v-state--error" aria-live="polite">Voice unavailable.{error ? ` (${error})` : ""}</span>}
      {!supports && <span className="l2v-state l2v-state--error">Voice not supported in this browser.</span>}
    </div>
  );
}
